import { useRef } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Card } from "@/components/ui/card";
import { Quote } from "lucide-react";
import Autoplay from "embla-carousel-autoplay";

const reviews = [
  {
    name: "Victor-Andrei Avasilcai",
    text: "Always an amazing experience and service! Never left Bo Barbers without looking fresh and having a smile on my face :) Jesus especially does an awesome job!",
    rating: 5
  },
  {
    name: "Sandra Beukema",
    text: "Mijn man en zoon worden hier al jaren geweldig geknipt, dankjewel team Bo Barbers. Allemaal top kappers!",
    rating: 5
  },
  {
    name: "Pedro Kolkowski",
    text: "Great experience at the salon. The place is nice and the service was excellent. Jesus was the one who cut my hair and he did an amazing job, exactly as I asked. Highly recommended!",
    rating: 5
  },
  {
    name: "Timo",
    text: "Ik ga altijd naar Jesus en het is altijd top. Nette kapsels en fades, nette service, vriendelijke mensen en altijd hoge kwaliteit. Zeker een aanrader.",
    rating: 5
  },
  {
    name: "Mitchell Gill",
    text: "Top barber. I've had a cut with I think everyone there, but most regularly Bo. They are all fantastic, worth a visit.",
    rating: 5
  },
  {
    name: "Ilir Kola",
    text: "Been getting haircuts there for over a year: great service, very friendly and professional. My go-to barber is Jesus, highly recommend him as he pays a lot of attention.",
    rating: 5
  },
  {
    name: "Peter de Vries",
    text: "First time coming here after looking for a new barber in Utrecht. Great and neat place near the centre of Utrecht. Great atmosphere and vibe.",
    rating: 5
  },
  {
    name: "sietse vanderwoude",
    text: "Great barbershop! Jesus always delivers, and is happy to think along in deciding how to shape the cut. Great atmosphere as well! Highly recommended.",
    rating: 5
  }
];

const ReviewsCarousel = () => {
  const plugin = useRef(
    Autoplay({ delay: 5000, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  return (
    <div className="w-full group">
      <div className="text-center mb-10 sm:mb-12 md:mb-16">
        <p className="text-foreground/60 uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-4 sm:mb-6 text-xs font-semibold">
          Reviews
        </p>
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 text-foreground leading-[1.2] px-4">
          WAT ONZE
          <br />
          KLANTEN ZEGGEN
        </h2>
        <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2">
          <div className="flex text-yellow-500">
            {[...Array(5)].map((_, i) => (
              <svg key={i} className="w-5 h-5 sm:w-6 sm:h-6 fill-current" viewBox="0 0 20 20">
                <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
              </svg>
            ))}
          </div>
          <span className="text-foreground/60 text-sm font-semibold">4.9</span>
        </div>
        <p className="text-foreground/60 text-xs sm:text-sm">Gebaseerd op 210 Google reviews</p>
      </div>

      <Carousel
        opts={{
          align: "start",
          loop: true,
          duration: 30,
        }}
        plugins={[plugin.current]}
        className="w-full max-w-5xl mx-auto px-4 sm:px-0"
      >
        <CarouselContent className="-ml-2 sm:-ml-4">
          {reviews.map((review, index) => (
            <CarouselItem key={index} className="pl-2 sm:pl-4 basis-full sm:basis-1/2 lg:basis-1/3">
              <Card className="relative p-5 sm:p-6 md:p-8 bg-gradient-to-br from-card via-card to-card/50 border-border/50 h-full transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 hover:border-primary/20 group overflow-hidden">
                {/* Decorative gradient overlay */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-3xl transition-opacity duration-500 opacity-0 group-hover:opacity-100"></div>
                
                {/* Quote icon */}
                <div className="relative mb-3 sm:mb-4">
                  <Quote className="w-8 h-8 sm:w-10 sm:h-10 text-primary/20 transform transition-all duration-500 group-hover:text-primary/40 group-hover:scale-110" />
                </div>
                
                {/* Stars */}
                <div className="flex text-yellow-500 mb-4 sm:mb-6 gap-0.5">
                  {[...Array(review.rating)].map((_, i) => (
                    <svg 
                      key={i} 
                      className="w-3 h-3 sm:w-4 sm:h-4 fill-current transition-transform duration-300 hover:scale-125" 
                      viewBox="0 0 20 20"
                      style={{ animationDelay: `${i * 50}ms` }}
                    >
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                
                {/* Review text */}
                <p className="relative text-foreground/80 mb-6 sm:mb-8 leading-relaxed text-xs sm:text-sm min-h-[60px] sm:min-h-[80px] italic">
                  "{review.text}"
                </p>
                
                {/* Divider */}
                <div className="w-10 sm:w-12 h-0.5 bg-gradient-to-r from-primary/40 to-transparent mb-3 sm:mb-4 transition-all duration-500 group-hover:w-16 sm:group-hover:w-20"></div>
                
                {/* Name */}
                <p className="font-bold text-xs sm:text-sm text-foreground transition-colors duration-300 group-hover:text-primary">
                  {review.name}
                </p>
              </Card>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="hidden sm:flex opacity-0 group-hover:opacity-100 transition-opacity duration-300 -left-4 md:-left-12 hover:bg-foreground hover:text-background" />
        <CarouselNext className="hidden sm:flex opacity-0 group-hover:opacity-100 transition-opacity duration-300 -right-4 md:-right-12 hover:bg-foreground hover:text-background" />
      </Carousel>
    </div>
  );
};

export default ReviewsCarousel;